import { createContext, FC, ReactNode, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { vendorActions } from 'src/ducks/actions'
import { getVendor } from 'src/ducks/selectors'
import { VendorType } from 'src/ducks/vendor/types'
import { VendorContextType } from './types'

const sortTerritoryList = <T extends { id: string }>(list: T[] | undefined) =>
  [...(list ?? [])].sort((a, b) => a.id.localeCompare(b.id))

const vendorPropertyChanged = (
  property: string,
  editedValue: unknown,
  originValue: unknown,
): boolean => {
  if (property === 'territoryList') {
    return JSON.stringify(sortTerritoryList(editedValue as { id: string }[] | undefined))
      !== JSON.stringify(sortTerritoryList(originValue as { id: string }[] | undefined))
  }

  return JSON.stringify(editedValue) !== JSON.stringify(originValue)
}

const initialState: VendorContextType = {
  editedVendor: null,
  setEditedVendor: () => null,
  hasChanges: false,
  loading: false,
  handleSave: () => { },
}

export const VendorContext = createContext<VendorContextType>(initialState)

export const VendorProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const vendor = useSelector(getVendor())
  const dispatch = useDispatch()
  const [editedVendor, setEditedVendor] = useState<Partial<VendorType>>(vendor)
  const [hasChanges, setHasChanges] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const edited =
      JSON.stringify({
        ...editedVendor,
        territoryList: sortTerritoryList(editedVendor?.territoryList),
      }) !== JSON.stringify({
        ...vendor,
        territoryList: sortTerritoryList(vendor?.territoryList),
      })
    setHasChanges(edited)
  }, [editedVendor, vendor])

  const handleSave = () => {
    const edited: any = editedVendor || {}
    const origin: any = vendor || {}
    const propertyList = [
      'tradeList',
      'firstName',
      'lastName',
      'companyName',
      'email',
      'address',
      'notes',
      'referral',
      'bgChecked',
      'bgPassed',
      'verificationStage',
      'territoryList',
      'lmsEnabled',
      'phone',
      'wcExemptTerritories'
    ]

    setLoading(true)
    let request: Partial<VendorType> = {}

    propertyList.forEach((property) => {
      if (vendorPropertyChanged(property, edited[property], origin[property])) {
        request = {
          ...request,
          [property]: edited[property],
        }
      }
    })

    if (JSON.stringify(request) !== JSON.stringify({})) {
      setHasChanges(false)
      dispatch(
        vendorActions.updateVendor(request, (succ) => {
          setLoading(false)
          setHasChanges(false)
          if (succ)
            toast.success('Changes saved successfully')
        })
      )
    } else {
      setLoading(false)
    }
  }

  return (
    <VendorContext.Provider
      value={{
        editedVendor,
        setEditedVendor,
        hasChanges,
        loading,
        handleSave,
      }}
    >
      {children}
    </VendorContext.Provider>
  )
}
